// ─────────────────────────────────────────────────────────────────────────────
// OKR Plans (coach) — BE enums → labels / tones (BE_HANDOFF.md §2.7–2.9).
// Keep every such mapping here.
// ─────────────────────────────────────────────────────────────────────────────

import { day, plural } from "./okrPlansCoachFormat";

// `/new-chat` feature slugs for coach chats (BE 2.8, 2.9).
export const COACH_FEATURE_SLUG = {
  CHECK_IN: "okr_plan_coach",
  FINAL: "okr_plan_coach_final",
};

// Plan status (BE 2.0): read-only for the coach (CD24).
export const STATUS_META = {
  ON_TRACK: { label: "On track", tone: "ok" },
  AT_RISK: { label: "At risk", tone: "warn" },
  OFF_TRACK: { label: "Off track", tone: "late" },
};
// CD44: no status yet reads "Not started".
export const statusMeta = (status) =>
  STATUS_META[status] || { label: "Not started", tone: "muted" };

// Review → the plan card note and the plan's Review fact tile (BE 2.2 review).
export const reviewNote = (plan) => {
  const r = plan.review || {};
  switch (r.state) {
    case "SUBMITTED":
      return { text: "Review submitted", tone: "warn" };
    case "OVERDUE":
      return { text: "Review overdue", tone: "late" };
    case "DUE_TODAY":
      return { text: "Review today", tone: "warn" };
    case "IN_PROGRESS":
      return { text: "Review in progress", tone: "muted" };
    case "REVIEWED":
      return { text: "Reviewed", tone: "ok" };
    case "NOT_REVIEWED":
      return { text: "Not reviewed", tone: "muted" };
    default:
      return null;
  }
};

export const reviewFact = (plan) => {
  const r = plan.review || {};
  const date = day(plan.review_date);
  switch (r.state) {
    case "REVIEWED":
      return {
        value: "Reviewed",
        caption: `Finalised ${day(r.finalised_at)}${r.ended_at_pct != null ? ` · ended at ${r.ended_at_pct}%` : ""}`,
      };
    case "SUBMITTED":
      return {
        value: "Submitted",
        caption: `${day(r.submitted_at)} · to finalise`,
        tone: "amber",
      };
    case "OVERDUE":
      return {
        value: `${plural(r.days_late || 0, "day")} late`,
        caption: `Was due ${date}`,
        tone: "red",
      };
    case "DUE_TODAY":
      return { value: "Today", caption: date, tone: "amber" };
    case "IN_PROGRESS":
      return { value: "In progress", caption: date, tone: "amber" };
    case "NOT_REVIEWED":
      return { value: "Not reviewed", caption: date };
    default:
      return {
        value: date,
        caption:
          plan.days_to_review != null
            ? `in ${plural(plan.days_to_review, "day")}`
            : "",
        tone:
          plan.days_to_review != null && plan.days_to_review <= 7
            ? "amber"
            : null,
      };
  }
};

// "Remind {owner}" only while the review is coming or open (BE 2.9, D217).
export const REMINDABLE = ["DUE_SOON", "DUE_TODAY", "OVERDUE", "IN_PROGRESS"];

// "Changes this week" values (BE 2.4).
export const progressValue = (item, value) =>
  item.target_type === "OBJECTIVE_STATUS"
    ? STATUS_META[value]?.label || "Not started"
    : `${value ?? 0}%`;

// Change-log `source` (BE 2.7).
export const CHANGE_SOURCE = {
  OKR_PLAN_PAGE: "Plan page",
  OKR_PLAN_CHAT: "Chat card",
  OKR_PLAN_REVIEW: "Final review",
  QOKR_BOARD: "Quarterly OKRs board",
  OLD_OKR_PAGE: "OKR page",
  SYSTEM: "System",
};

// Summary-change card `field` (BE 2.9): only ever the executive summary
// (BE FE68 / D265; CD47) — no `target_id`.
export const SUMMARY_FIELD = {
  executive_summary: "Executive summary",
};
